// Temper strip reconstruction.
//
// The strip has N_LOTS cells; cell i covers curve supply [i*LOT, (i+1)*LOT). A cell's fill is the
// share of that range currently in circulation (from the live `sold`). Its colour is the
// difficulty met by the forge that most recently filled any part of it that is still filled.
//
// Rule (exact, needs only Forged events + the current `sold`): every forge adds supply
// [sold - amount, sold) on top of the curve; sells only ever remove supply from the top. So,
// walking forges newest -> oldest with m = min(current sold, starts of all newer forges), the part
// of a forge that is still in circulation is [start, min(end, m)) — and those surviving parts
// tile [m, sold) with the newest on top. Each filled cell takes the newest surviving forge that
// overlaps it. A lot that was sold back therefore returns to empty, and when it is forged again it
// takes the new forge's colour. Cells below the oldest forge we have seen stay "unknown" until
// more history is loaded (resolved === false).
import { LOT, N_LOTS } from '@hashcurve/protocol';

export interface ForgeRecord {
  id: string;
  /** curve supply in circulation right after this forge */
  sold: bigint;
  /** tokens bought by this forge */
  amount: bigint;
  /** difficulty met (expected hashes) */
  difficulty: number;
}

export interface StripCell {
  /** 0..1 share of the lot currently in circulation */
  fill: number;
  /** difficulty met by the forge that most recently filled it; null = empty or not yet known */
  difficulty: number | null;
  forgeId: string | null;
}

export interface Strip {
  cells: StripCell[];
  /** true when every filled cell has a known forge */
  resolved: boolean;
}

export function cellFill(i: number, sold: bigint): number {
  const lo = BigInt(i) * LOT;
  if (sold <= lo) return 0;
  if (sold >= lo + LOT) return 1;
  return Number(((sold - lo) * 10_000n) / LOT) / 10_000;
}

/** @param forgesNewestFirst forge events ordered newest first */
export function buildStrip(forgesNewestFirst: readonly ForgeRecord[], sold: bigint, lots = N_LOTS): Strip {
  const cells: StripCell[] = Array.from({ length: lots }, (_, i) => ({ fill: cellFill(i, sold), difficulty: null, forgeId: null }));
  let m = sold;
  for (const f of forgesNewestFirst) {
    if (m <= 0n) break;
    const start = f.sold - f.amount;
    const end = f.sold < m ? f.sold : m;
    if (start < end) {
      const first = Number(start / LOT);
      const last = Math.min(lots - 1, Number((end - 1n) / LOT));
      for (let i = first; i <= last; i++) {
        const c = cells[i]!;
        if (c.forgeId === null) {
          c.forgeId = f.id;
          c.difficulty = f.difficulty;
        }
      }
    }
    if (start < m) m = start;
  }
  return { cells, resolved: m <= 0n };
}

/** Merge forge lists by id, newest first (by `order`, e.g. blockNumber * 1e6 + logIndex). */
export function mergeForges<T extends ForgeRecord & { order: number }>(a: readonly T[], b: readonly T[]): T[] {
  const byId = new Map<string, T>();
  for (const f of a) byId.set(f.id, f);
  for (const f of b) byId.set(f.id, f);
  return [...byId.values()].sort((x, y) => y.order - x.order);
}
